import { useState, type FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast, SpinAnimation } from "@twioku/common_components";
import { useRequireAgreedAnswerer } from "../features/answerer/hooks/useRequireAgreedAnswerer";
import { AddQuestionInput } from "../features/question/zod/AddQuestion";
import { Path } from "../routes/PagePath";
import { QuestionForm } from "../features/question/components/QuestionForm";
import { useUserQuestion } from "../features/question/hooks/useUserQuestion";
import { useUpdateUserQuestion } from "../features/question/hooks/useUpdateUserQuestion";
import { useDeleteUserQuestion } from "../features/question/hooks/useDeleteUserQuestion";

export const UpdateQuestionPage: FC = () => {
    const params = useParams();
    const id = params.id as string;
    const answererId = params.answererId as string;
    const {
        question,
        isLoading: isGetQuestionLoading,
        clearCache,
    } = useUserQuestion(id);
    const {
        answerer,
        isLoading: isGetAnswererLoading,
        isAgreed,
    } = useRequireAgreedAnswerer(answererId);
    const { updateUserQuestion } = useUpdateUserQuestion(id);
    const { deleteUserQuestion } = useDeleteUserQuestion();
    const navigate = useNavigate();
    const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

    const handleClickUpdateQuestionButton = async (input: AddQuestionInput) => {
        setIsUpdateLoading(true);
        await updateUserQuestion.mutate(
            { title: input.title },
            {
                onSuccess: () => {
                    showToast("質問を更新しました");
                    clearCache();
                    navigate(`${Path.questionList}/${input.answererId}`, {
                        replace: true,
                    });
                },
                onError: () => {
                    showToast(
                        "エラーが発生しました。\n管理者にお問合せください。",
                        "error"
                    );
                },
                onSettled: () => {
                    setIsUpdateLoading(false);
                },
            }
        );
    };

    const handleClickDeleteQuestionButton = async () => {
        setIsDeleteLoading(true);
        if (window.confirm("質問を削除しますか？")) {
            await deleteUserQuestion.mutate(id, {
                onSuccess: () => {
                    showToast("質問を削除しました");
                    clearCache();
                    navigate(`${Path.questionList}/${answererId}`, {
                        replace: true,
                    });
                },
                onError: () => {
                    showToast(
                        "エラーが発生しました。\n管理者にお問合せください。",
                        "error"
                    );
                },
                onSettled: () => {
                    setIsDeleteLoading(false);
                },
            });
            return;
        }
        setIsDeleteLoading(false);
    };

    if (isGetQuestionLoading || isGetAnswererLoading) {
        return (
            <div className="absolute inset-0 flex items-center justify-center">
                <SpinAnimation />
            </div>
        );
    }

    if (!answerer || !question || !isAgreed) {
        navigate(Path.notFound, { replace: true });
        return null;
    }

    return (
        <QuestionForm
            answerer={answerer}
            handleClickSaveQuestion={handleClickUpdateQuestionButton}
            saveQuestionButtonName="質問を更新する"
            isSaveLoading={isUpdateLoading}
            defaultValue={{ title: question.title }}
            mode="update"
            handleClickDeleteQuestion={handleClickDeleteQuestionButton}
            isDeleteLoading={isDeleteLoading}
        />
    );
};
